import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Player, Shortlist } from "@/lib/types";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { SoccerPitch } from "@/components/ui/soccer-pitch";
import { useUnitsPreference } from "@/hooks/useUnitsPreference";
import { convertHeight, convertWeight } from "@/lib/unitConversion";
import { countries } from "@/lib/filter-data";
import { MessageCircle, ToggleLeft, ToggleRight, Clock, Mail } from "lucide-react";
import PlayerEmailModal from "./PlayerEmailModal";
import { getPlayerMessages } from "@/lib/api/notifications";
import { PlayerMessage } from "@/lib/types";
import { formatDistanceToNow, format } from "date-fns";

/* ------------------------------------------------------------------ */
interface PlayerDetailModalProps {
  player: Player;
  isOpen: boolean;
  onClose: () => void;
  shortlists: Shortlist[];
}

interface PlayerEditData {
  playing_level?: string;
  age?: number;
  current_club?: string;
  gpa?: string | number;
  height?: string;
  weight?: string;
  nationality?: string;
  hs_grad_year?: number;
  previous_clubs?: string[];
  university_grad_year?: number;
  international_caps?: Array<Record<string, any>>;
  distinguishments?: string[];
  jersey_number?: string;
  event?: string[];
}

/* ------------------------------------------------------------------ */
export default function PlayerDetailModal({
  player,
  isOpen,
  onClose,
  shortlists,
}: PlayerDetailModalProps) {
  const { toast } = useToast();
  const { isMetric } = useUnitsPreference();
  const [selectedShortlist, setSelectedShortlist] = useState<number | null>(
    null
  );
  const [notes, setNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  
  // Local player state to reflect immediate updates
  const [currentPlayer, setCurrentPlayer] = useState<Player>(player);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<PlayerEditData>({});
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [tempNewCap, setTempNewCap] = useState({ level: "", caps: "" });
  const [tempNewDistinguishment, setTempNewDistinguishment] = useState("");
  const [tempNewEvent, setTempNewEvent] = useState("");
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // Email comparison state
  const [showPreviousEmail, setShowPreviousEmail] = useState(false);

  // Update local player state when prop changes
  useEffect(() => {
    setCurrentPlayer(player);
  }, [player]);

  // Initialize edit data when entering edit mode
  useEffect(() => {
    if (isEditMode && currentPlayer) {
      // Convert height and weight to user's preferred units for editing
      const convertedHeight = currentPlayer.height ? convertHeight(currentPlayer.height, isMetric) : "";
      const convertedWeight = currentPlayer.weight ? convertWeight(currentPlayer.weight, isMetric) : "";
      
      setEditData({
        playing_level: currentPlayer.playing_level || "",
        age: currentPlayer.age || undefined,
        current_club: currentPlayer.current_club || "",
        gpa: currentPlayer.gpa || "",
        height: convertedHeight,
        weight: convertedWeight,
        nationality: currentPlayer.nationality || "",
        hs_grad_year: currentPlayer.hs_grad_year || undefined,
        previous_clubs: currentPlayer.previous_clubs || [],
        university_grad_year: currentPlayer.university_grad_year || undefined,
        international_caps: currentPlayer.international_caps || [],
        distinguishments: currentPlayer.distinguishments || [],
        jersey_number: currentPlayer.jersey_number || "",
        event: currentPlayer.event || []
      });
    }
  }, [isEditMode, currentPlayer, isMetric]);

  /* ----------------------------- queries ---------------------------- */
  // Query for individual player data to ensure we always have fresh data
  const playerQuery = useQuery({
    queryKey: [`/api/players/${player.id}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/${player.id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch player data");
      }
      return response.json();
    },
    enabled: isOpen && !!player.id,
    initialData: player, // Use the prop as initial data
    staleTime: 0, // Always refetch to ensure fresh data
  });

  const coachNotesQuery = useQuery({
    queryKey: [`/api/players/coach-notes/${currentPlayer.id}`],
    queryFn: async () => {
      try {
        const r = await apiRequest("GET", `/api/players/coach-notes/${currentPlayer.id}`);
        if (!r.ok) {
          // If it's an auth error, return empty content instead of throwing
          if (r.status === 401 || r.status === 403) {
            console.warn('Authentication error fetching coach notes, using empty content');
            return { content: "" };
          }
          throw new Error("Failed to fetch coach notes");
        }
        return r.json();
      } catch (error) {
        // Handle network errors gracefully - return empty content
        console.warn('Failed to fetch coach notes:', error);
        return { content: "" };
      }
    },
    enabled: isOpen && !!currentPlayer.id,
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      // Only retry once for other errors
      return failureCount < 1;
    },
    retryDelay: 1000,
  });

  // Fetch player messages
  const messagesQuery = useQuery({
    queryKey: ["player-messages", { player_id: player.id }],
    queryFn: async () => {
      console.log(`Fetching messages for player ${player.name} (ID: ${player.id})`);
      const messages = await getPlayerMessages({ player_id: player.id, limit: 5 });
      console.log(`Messages received for player ${player.name}:`, messages);
      return messages;
    },
    enabled: isOpen && !!player.id,
  });

  // Update currentPlayer when fresh data is available
  useEffect(() => {
    if (playerQuery.data && !playerQuery.isLoading) {
      setCurrentPlayer(playerQuery.data);
      

    }
  }, [playerQuery.data, playerQuery.isLoading]);

  useEffect(() => {
    if (coachNotesQuery.data) setNotes(coachNotesQuery.data.content || "");
  }, [coachNotesQuery.data, currentPlayer.id]);

  // Mark update as read when modal opens for player with unread update
  useEffect(() => {
    if (isOpen && currentPlayer.has_unread_update && currentPlayer.id) {
      markUpdateAsReadMutation.mutate(currentPlayer.id);
    }
  }, [isOpen, currentPlayer.id]);

  /* --------------------------- mutations --------------------------- */
  const editPlayerMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const r = await apiRequest("PUT", `/api/players/${currentPlayer.id}/manual-edit`, {
        updates
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.detail || "Failed to update player data");
      }
      return r.json();
    },
    onSuccess: async (data, variables) => {
      toast({ 
        title: "Success", 
        description: data.message || "Data updated successfully and error reported to Bloom Team"
      });
      setIsEditMode(false);
      setShowSaveConfirmation(false);
      // Update local state with new player data
      if (data.player) {
        setCurrentPlayer(data.player);
      } else if (data) {
        setCurrentPlayer(data);
      }
      // Instantly refresh the player list in the table
      queryClient.invalidateQueries({ queryKey: ["/api/players/"] });
      // Refetch the specific player data to update the modal immediately
      await playerQuery.refetch();
      // Also refresh bulk player data in background for consistency
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      // Force refresh of the data coordinator which provides player data to the dashboard
      queryClient.refetchQueries({ queryKey: ["/api/players/"] });
    },
    onError: (e: any) => {
      toast({
        title: "Error",
        description: e.message || "Failed to update player data",
        variant: "destructive",
      });
      setShowSaveConfirmation(false);
    },
  });

  const addToShortlistMutation = useMutation({
    mutationFn: async ({
      shortlistId,
      playerId,
    }: {
      shortlistId: number;
      playerId: number;
    }) => {
      const response = await apiRequest(
        "POST",
        `/api/shortlists/${shortlistId}/players`,
        { playerId }
      );
      
      if (!response.ok) {
        let errorMessage = "Failed to add player to shortlist";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      return response.json();
    },
    onSuccess: (_, vars) => {
      toast({ 
        title: "Success", 
        description: "Player added to shortlist successfully",
        variant: "default"
      });
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/shortlists/${vars.shortlistId}`],
      });
      setSelectedShortlist(null);
    },
    onError: (error: any) => {
      console.error('Shortlist error:', error);
      
      const errorMessage = error?.message || "Unknown error occurred";
      
      // Check for specific error types
      const isAuthError = errorMessage.includes('401') || 
                         errorMessage.includes('403') || 
                         errorMessage.includes('Unauthorized') || 
                         errorMessage.includes('Forbidden') ||
                         errorMessage.includes('Session Has Expired') ||
                         errorMessage.includes('authentication');
      
      const isDuplicateError = errorMessage.toLowerCase().includes("already") ||
                              errorMessage.toLowerCase().includes("duplicate");
      
      if (isAuthError) {
        toast({
          title: "❌ Session Expired",
          description: "Your session has expired. Please refresh the page and try again.",
          variant: "destructive",
        });
      } else if (isDuplicateError) {
        toast({
          title: "Already Added",
          description: "This player is already in the selected shortlist.",
          variant: "default",
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to add player to shortlist: ${errorMessage}`,
          variant: "destructive",
        });
      }
    },
  });

  const saveNotesMutation = useMutation({
    mutationFn: async ({
      playerId,
      content,
    }: {
      playerId: number;
      content: string;
    }) => {
      const r = await apiRequest("POST", "/api/players/coach-notes", {
        playerId,
        content,
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Failed to save notes");
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Notes saved" });
      queryClient.invalidateQueries({
        queryKey: [`/api/players/coach-notes/${player.id}`],
      });
      setIsSavingNotes(false);
    },
    onError: (e: any) => {
      // Check if it's an authentication error
      const isAuthError = e?.message?.includes('401') || e?.message?.includes('403') || 
                         e?.message?.includes('Unauthorized') || e?.message?.includes('Forbidden');
      
      toast({
        title: "Error",
        description: isAuthError 
          ? "Your session has expired. Please refresh the page and try again." 
          : e.message || "Failed to save notes",
        variant: "destructive",
      });
      setIsSavingNotes(false);
    },
  });

  const markUpdateAsReadMutation = useMutation({
    mutationFn: async (playerId: number) => {
      const r = await apiRequest("POST", `/api/players/${playerId}/mark-read`);
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message || "Failed to mark update as read");
      }
      return r.json();
    },
    onSuccess: () => {
      // Update local player state
      setCurrentPlayer(prev => ({
        ...prev,
        has_unread_update: false
      }));
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["player-updates"] });
      queryClient.invalidateQueries({ queryKey: ["player-updates-count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players/"] });
    },
    onError: (e: any) => {
      console.error("Failed to mark update as read:", e);
    },
  });

  /* --------------------------- edit helpers --------------------------- */
  const handleEditChange = (field: keyof PlayerEditData, value: any) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdits = () => {
    // Prepare updates, filtering out empty values
    const updates: Record<string, any> = {};
    
    Object.entries(editData).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        // Convert empty arrays to null for proper handling
        if (Array.isArray(value) && value.length === 0) {
          updates[key] = null;
        } else {
          updates[key] = value;
        }
      }
    });

    // Add unit preference flag for height and weight conversion
    if (updates.height !== undefined || updates.weight !== undefined) {
      updates._units_used = isMetric ? 'metric' : 'imperial';
    }



    if (Object.keys(updates).length === 0) {
      toast({
        title: "No Changes",
        description: "No changes were made to save.",
        variant: "default"
      });
      return;
    }

    editPlayerMutation.mutate(updates);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
  };

  const addInternationalCap = () => {
    if (tempNewCap.level && tempNewCap.caps) {
      const newCap = { [tempNewCap.level]: tempNewCap.caps };
      setEditData(prev => ({
        ...prev,
        international_caps: [...(prev.international_caps || []), newCap]
      }));
      setTempNewCap({ level: "", caps: "" });
    }
  };

  const removeInternationalCap = (index: number) => {
    setEditData(prev => ({
      ...prev,
      international_caps: prev.international_caps?.filter((_, i) => i !== index) || []
    }));
  };

  const addDistinguishment = () => {
    if (tempNewDistinguishment.trim()) {
      setEditData(prev => ({
        ...prev,
        distinguishments: [...(prev.distinguishments || []), tempNewDistinguishment.trim()]
      }));
      setTempNewDistinguishment("");
    }
  };

  const removeDistinguishment = (index: number) => {
    setEditData(prev => ({
      ...prev,
      distinguishments: prev.distinguishments?.filter((_, i) => i !== index) || []
    }));
  };

  const addPreviousClub = (clubName: string) => {
    if (clubName.trim()) {
      setEditData(prev => ({
        ...prev,
        previous_clubs: [...(prev.previous_clubs || []), clubName.trim()]
      }));
    }
  };

  const removePreviousClub = (index: number) => {
    setEditData(prev => ({
      ...prev,
      previous_clubs: prev.previous_clubs?.filter((_, i) => i !== index) || []
    }));
  };

  // Height format validation
  const validateHeightFormat = (height: string): boolean => {
    if (!height.trim()) return true; // Empty is valid
    
    if (isMetric) {
      // Metric format: "180 cm" or just "180"
      return /^\d{2,3}(\s?cm)?$/.test(height.trim());
    } else {
      // Imperial format: "6'0\"" or "6'0" or "6 0"
      return /^\d{1,2}['']?\s?\d{0,2}[""]?$/.test(height.trim());
    }
  };

  const validateWeightFormat = (weight: string): boolean => {
    if (!weight.trim()) return true; // Empty is valid
    
    if (isMetric) {
      // Metric format: "75 kg" or just "75"
      return /^\d{2,3}(\s?kg)?$/.test(weight.trim());
    } else {
      // Imperial format: "165 lbs" or just "165"
      return /^\d{2,3}(\s?lbs?)?$/.test(weight.trim());
    }
  };

  /* ----------------------------- UI ------------------------------- */
  if (!player) return null;

  // Show error state if there's an authentication issue
  if (coachNotesQuery.error && 
      (coachNotesQuery.error.message?.includes('401') || 
       coachNotesQuery.error.message?.includes('403'))) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg">
              <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
              Session Expired
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-neutral-600">
              Your session has expired. Please refresh the page to continue viewing player details.
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                <i className="fas fa-refresh mr-2"></i>
                Refresh Page
              </Button>
              <Button 
                variant="outline" 
                onClick={onClose}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show loading state while fetching notes
  if (coachNotesQuery.isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg">
              <AvatarPlaceholder
                name={player.name}
                className="h-8 w-8 mr-3"
              />
              Loading Player Details...
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <i className="fas fa-spinner fa-spin text-2xl text-primary"></i>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-7xl max-h-[95vh] overflow-y-auto p-3 text-base">
          {/* HEADER */}
          <DialogHeader className="pb-0 pt-4">
            <DialogTitle className="flex items-center justify-between text-xl">
              <div className="flex items-center">
                <AvatarPlaceholder
                  name={currentPlayer.name}
                  className="h-10 w-10 mr-3"
                />
                <div className="flex flex-col">
                  <span className="font-bold tracking-tight">{currentPlayer.name}</span>
                  {currentPlayer.email && (
                    <span className="text-sm text-neutral-600 flex items-center">
                      <i className="fas fa-envelope text-blue-500 mr-1.5 text-xs" />
                      {currentPlayer.email}
                    </span>
                  )}
                </div>
                {currentPlayer.has_unread_update && (
                  <Badge variant="default" className="ml-3 flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" />
                    New Message
                  </Badge>
                )}
                {(currentPlayer.email_update_count ?? 0) > 1 && (
                  <Badge variant="outline" className="ml-2">
                    {(currentPlayer.email_update_count ?? 0)} messages total
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Edit Mode Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditMode(!isEditMode)}
                  className={`h-8 px-3 ${isEditMode ? 'bg-blue-100 text-blue-600' : ''}`}
                  title={isEditMode ? "Exit edit mode" : "Edit player data"}
                >
                  <i className={`fas ${isEditMode ? 'fa-times' : 'fa-edit'} text-sm mr-2`} />
                  {isEditMode ? "Cancel" : "Edit Incorrect Data"}
                </Button>
                
                <div className="bg-amber-100 px-3 py-1 rounded-full text-amber-800 text-sm font-semibold border border-amber-200 shadow-sm">
                  {currentPlayer.specific_positions && currentPlayer.specific_positions.length > 0 
                    ? currentPlayer.specific_positions[0] 
                    : currentPlayer.position && currentPlayer.position.length > 0 
                      ? currentPlayer.position[0] 
                      : "Unknown"}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {/* Edit Mode Actions */}
          {isEditMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fas fa-edit text-blue-600" />
                  <span className="text-blue-800 font-medium">Edit Mode</span>
                  <span className="text-blue-600 text-sm">Make changes to player data below. Errors will be reported to our team.</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={editPlayerMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowSaveConfirmation(true)}
                    disabled={editPlayerMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {editPlayerMutation.isPending ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* TOP GRID */}
          <div className="bg-neutral-50 p-3 rounded-lg border border-neutral-200 shadow-sm mb-3">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              {/* 1 ▸ Stats  (10/12) */}
              <div className="lg:col-span-9 grid grid-cols-1 gap-3">
                {/* row 1 */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ["Playing Level", currentPlayer.playing_level, "playing_level"] as const,
                    ["Age", currentPlayer.age, "age"] as const,
                    ["Current Club", currentPlayer.current_club, "current_club"] as const,
                    ["GPA", currentPlayer.gpa, "gpa"] as const,
                  ].map(([label, value, field]) => (
                    <div
                      key={label}
                      className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm"
                    >
                      <h4 className="text-sm font-semibold text-neutral-600">
                        {label}
                      </h4>
                      {isEditMode ? (
                        <Input
                          value={String(editData[field as keyof PlayerEditData] || "")}
                          onChange={(e) => {
                            const val = field === "age" ? (e.target.value ? parseInt(e.target.value) : undefined) : e.target.value;
                            handleEditChange(field as keyof PlayerEditData, val);
                          }}
                          type={field === "age" ? "number" : "text"}
                          className="mt-1 h-8 text-sm"
                          placeholder={`Enter ${label.toLowerCase()}`}
                        />
                      ) : (
                        <p className="font-bold text-slate-800">
                          {value || "N/A"}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* row 1.5 - Physical Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Height */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      Height {(
                        <span className="text-xs text-neutral-500 font-normal">
                          ({isMetric ? "cm" : "ft'in\""})
                        </span>
                      )}
                    </h4>
                    {isEditMode ? (
                      <div>
                        <Input
                          value={editData.height || ""}
                          onChange={(e) => handleEditChange("height", e.target.value)}
                          type="text"
                          className={`mt-1 h-8 text-sm ${
                            editData.height && !validateHeightFormat(editData.height) 
                              ? "border-red-500 focus:border-red-500" 
                              : ""
                          }`}
                          placeholder={isMetric ? "e.g., 180 cm" : "e.g., 6'0\""}
                        />
                        {editData.height && !validateHeightFormat(editData.height) && (
                          <p className="text-xs text-red-500 mt-1">
                            {isMetric ? "Format: 180 cm or 180" : "Format: 6'0\" or 6'0"}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.height ? convertHeight(currentPlayer.height, isMetric).replace(/\s?cm/g, '') : "N/A"}
                      </p>
                    )}
                  </div>

                  {/* Weight */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      Weight {(
                        <span className="text-xs text-neutral-500 font-normal">
                          ({isMetric ? "kg" : "lbs"})
                        </span>
                      )}
                    </h4>
                    {isEditMode ? (
                      <div>
                        <Input
                          value={editData.weight || ""}
                          onChange={(e) => handleEditChange("weight", e.target.value)}
                          type="text"
                          className={`mt-1 h-8 text-sm ${
                            editData.weight && !validateWeightFormat(editData.weight) 
                              ? "border-red-500 focus:border-red-500" 
                              : ""
                          }`}
                          placeholder={isMetric ? "e.g., 75 kg" : "e.g., 165 lbs"}
                        />
                        {editData.weight && !validateWeightFormat(editData.weight) && (
                          <p className="text-xs text-red-500 mt-1">
                            {isMetric ? "Format: 75 kg or 75" : "Format: 165 lbs or 165"}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.weight ? convertWeight(currentPlayer.weight, isMetric).replace(/\s?(cm|kg|lbs)/g, '') : "N/A"}
                      </p>
                    )}
                  </div>

                  {/* Nationality */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      Nationality
                    </h4>
                    {isEditMode ? (
                        <Select onValueChange={(value) => handleEditChange("nationality", value === "none" ? "" : value)} value={editData.nationality || "none"}>
                          <SelectTrigger className="mt-1 h-8 text-sm">
                            <SelectValue placeholder="Select nationality" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            <SelectItem value="none">No nationality</SelectItem>
                            {countries.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.nationality || "N/A"}
                      </p>
                    )}
                  </div>

                  {/* HS Grad Year */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      HS Grad Year
                    </h4>
                    {isEditMode ? (
                      <Input
                        value={editData.hs_grad_year || ""}
                        onChange={(e) => handleEditChange("hs_grad_year", e.target.value ? parseInt(e.target.value) : undefined)}
                        type="number"
                        className="mt-1 h-8 text-sm"
                        placeholder="Enter HS grad year"
                      />
                    ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.hs_grad_year || "N/A"}
                      </p>
                    )}
                  </div>
                </div>

                {/* row 1.75 - Previous Clubs with University Grad Year and Jersey Number */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Previous Clubs */}
                  <div className="col-span-2 bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      Previous Clubs
                    </h4>
                    {isEditMode ? (
                      <div className="mt-1 space-y-1">
                        {editData.previous_clubs?.map((club, index) => (
                          <div key={index} className="flex items-center gap-1">
                            <Input
                              value={club}
                              onChange={(e) => {
                                const updated = [...(editData.previous_clubs || [])];
                                updated[index] = e.target.value;
                                handleEditChange("previous_clubs", updated);
                              }}
                              className="h-6 text-xs"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removePreviousClub(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <i className="fas fa-times text-xs" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addPreviousClub("New Club")}
                          className="h-6 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <i className="fas fa-plus mr-1" />
                          Add Club
                        </Button>
                      </div>
                    ) : (
                      <p className="font-bold text-slate-800">
                        {player.previous_clubs && player.previous_clubs.length > 0
                          ? player.previous_clubs.join(', ')
                          : 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* University Grad Year */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      University Grad Year
                    </h4>
                    {isEditMode ? (
                      <Input
                        value={editData.university_grad_year || ""}
                        onChange={(e) => handleEditChange("university_grad_year", e.target.value ? parseInt(e.target.value) : "")}
                        type="number"
                        className="mt-1 h-8 text-sm"
                        placeholder="Enter university grad year"
                      />
                    ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.university_grad_year || "N/A"}
                      </p>
                    )}
                  </div>

                  {/* Jersey Number */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      Jersey Number
                    </h4>
                    {isEditMode ? (
                      <Input
                        value={editData.jersey_number || ""}
                        onChange={(e) => handleEditChange("jersey_number", e.target.value)}
                        type="text"
                        className="mt-1 h-8 text-sm"
                        placeholder="Enter jersey number"
                      />
                    ) : (
                      <p className="font-bold text-slate-800">
                        {currentPlayer.jersey_number || "N/A"}
                      </p>
                    )}
                  </div>
                </div>

                {/* row 2 */}
                <div className="grid grid-cols-3 gap-3">
                  {/* International Caps */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600">
                      International Caps
                    </h4>
                    {isEditMode ? (
                      <div className="mt-1 space-y-2">
                        {editData.international_caps?.map((cap, index) => {
                          const entries = Object.entries(cap);
                          return entries.map(([level, caps], entryIndex) => (
                            <div key={`${index}-${entryIndex}`} className="flex items-center gap-1">
                              <Input
                                value={level}
                                onChange={(e) => {
                                  const updated = [...(editData.international_caps || [])];
                                  const newCap = { [e.target.value]: caps };
                                  updated[index] = newCap;
                                  handleEditChange("international_caps", updated);
                                }}
                                placeholder="Level (e.g., U18)"
                                className="h-6 text-xs flex-1"
                              />
                              <Input
                                value={caps}
                                onChange={(e) => {
                                  const updated = [...(editData.international_caps || [])];
                                  const newCap = { [level]: e.target.value };
                                  updated[index] = newCap;
                                  handleEditChange("international_caps", updated);
                                }}
                                placeholder="Caps"
                                className="h-6 text-xs w-16"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeInternationalCap(index)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              >
                                <i className="fas fa-times text-xs" />
                              </Button>
                            </div>
                          ));
                        })}
                        <div className="flex gap-1">
                          <Input
                            value={tempNewCap.level}
                            onChange={(e) => setTempNewCap(prev => ({ ...prev, level: e.target.value }))}
                            placeholder="Level (e.g., U18)"
                            className="h-6 text-xs flex-1"
                          />
                          <Input
                            value={tempNewCap.caps}
                            onChange={(e) => setTempNewCap(prev => ({ ...prev, caps: e.target.value }))}
                            placeholder="Caps"
                            className="h-6 text-xs w-16"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={addInternationalCap}
                            disabled={!tempNewCap.level || !tempNewCap.caps}
                            className="h-6 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <i className="fas fa-plus" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {player.international_caps?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {player.international_caps
                              .filter((cap) => cap && typeof cap === 'object') // Filter out invalid cap objects
                              .map((cap, index) => {
                                // Handle the actual data structure: [{"U15":"3"},{"U16":"3"}]
                                const entries = Object.entries(cap);
                                return entries.map(([level, caps], entryIndex) => (
                                  <span
                                    key={`${index}-${entryIndex}-${level}`}
                                    className="bg-blue-100 text-blue-800 px-1.5 py-0.5 text-xs font-medium rounded-full"
                                  >
                                    {level}: <span className="font-bold">{caps}</span>
                                  </span>
                                ));
                              })
                              .flat() // Flatten the array since we're mapping over entries
                            }
                          </div>
                        ) : (
                          <p className="text-xs text-neutral-500">None</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Distinguishments */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600 mb-2">
                      Distinguishments
                    </h4>
                    {isEditMode ? (
                      <div className="space-y-1">
                        {editData.distinguishments?.map((distinguishment, index) => (
                          <div key={index} className="flex items-center gap-1">
                            <Input
                              value={distinguishment}
                              onChange={(e) => {
                                const updated = [...(editData.distinguishments || [])];
                                updated[index] = e.target.value;
                                handleEditChange("distinguishments", updated);
                              }}
                              className="h-6 text-xs"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDistinguishment(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <i className="fas fa-times text-xs" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-1">
                          <Input
                            value={tempNewDistinguishment}
                            onChange={(e) => setTempNewDistinguishment(e.target.value)}
                            placeholder="Add new distinguishment"
                            className="h-6 text-xs flex-1"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                addDistinguishment();
                              }
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={addDistinguishment}
                            disabled={!tempNewDistinguishment.trim()}
                            className="h-6 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <i className="fas fa-plus" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {player.distinguishments?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {player.distinguishments.map((d, i) => {
                              const palette = [
                                "bg-amber-100 text-amber-800",
                                "bg-green-100 text-green-800",
                                "bg-blue-100 text-blue-800",
                                "bg-purple-100 text-purple-800",
                                "bg-red-100 text-red-800",
                              ];
                              return (
                                <span
                                  key={d}
                                  className={`px-3 py-1.5 rounded-lg full text-xs font-medium ${
                                    palette[i % palette.length]
                                  }`}
                                >
                                  {d}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-neutral-500">None</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Events */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                    <h4 className="text-sm font-semibold text-neutral-600 mb-2">
                      Event(s)
                    </h4>
                    {isEditMode ? (
                      <div className="space-y-1">
                        {(editData.event || []).map((event, index) => (
                          <div key={index} className="flex items-center gap-1">
                            <Input
                              value={event}
                              onChange={(e) => {
                                const updated = [...(editData.event || [])];
                                updated[index] = e.target.value;
                                handleEditChange("event", updated);
                              }}
                              className="h-6 text-xs"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = [...(editData.event || [])];
                                updated.splice(index, 1);
                                handleEditChange("event", updated);
                              }}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <i className="fas fa-times text-xs" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-1">
                          <Input
                            value={tempNewEvent || ""}
                            onChange={(e) => setTempNewEvent(e.target.value)}
                            placeholder="Add new event"
                            className="h-6 text-xs flex-1"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                if (tempNewEvent && tempNewEvent.trim()) {
                                  handleEditChange("event", [...(editData.event || []), tempNewEvent.trim()]);
                                  setTempNewEvent("");
                                }
                              }
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (tempNewEvent && tempNewEvent.trim()) {
                                handleEditChange("event", [...(editData.event || []), tempNewEvent.trim()]);
                                setTempNewEvent("");
                              }
                            }}
                            disabled={!tempNewEvent || !tempNewEvent.trim()}
                            className="h-6 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <i className="fas fa-plus" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {player.event && player.event.length ? (
                          <div className="flex flex-wrap gap-2">
                            {player.event.map((ev, i) => {
                              const palette = [
                                "bg-yellow-100 text-yellow-800",
                                "bg-pink-100 text-pink-800",
                                "bg-blue-100 text-blue-800",
                                "bg-green-100 text-green-800",
                                "bg-purple-100 text-purple-800",
                              ];
                              return (
                                <span
                                  key={ev}
                                  className={`px-3 py-1.5 rounded-lg full text-xs font-medium ${
                                    palette[i % palette.length]
                                  }`}
                                >
                                  {ev}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-neutral-500">None</p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Rest of the modal content - notes + resources + email content remains the same... */}
                {/* row 3  (notes) */}
                <div className="grid grid-cols-1 gap-3">
                  {/* notes */}
                  <div className="bg-white p-2 rounded-lg border border-neutral-200">
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="text-sm font-semibold text-neutral-800">
                        Coach's Notes
                      </h4>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2"
                        disabled={isSavingNotes || saveNotesMutation.isPending}
                        onClick={() =>
                          saveNotesMutation.mutate({
                            playerId: player.id,
                            content: notes,
                          })
                        }
                      >
                        {isSavingNotes || saveNotesMutation.isPending
                          ? "Saving..."
                          : "Save"}
                      </Button>
                    </div>
                    <Textarea
                      className="h-[90px] text-sm"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this player..."
                    />
                  </div>
                </div>
              </div>

              {/* 2 ▸ Positions  (2/12) */}
              <div className="lg:col-span-3 bg-white rounded-lg border border-neutral-200 shadow-sm">
                <div className="p-2">
                  <h4 className="text-sm font-semibold text-neutral-800 mb-2 flex items-center">
                    <i className="fas fa-futbol text-green-600 mr-1.5" />
                    Player Position
                  </h4>
                  
                  <div className="bg-neutral-50 p-4 rounded-lg">
                    <div className="text-sm text-neutral-600 mb-1">Position</div>
                    <div className="text-lg font-medium text-neutral-900">
                      {player.specific_positions && player.specific_positions.length > 0 
                        ? player.specific_positions.join(", ") 
                        : player.position && player.position.length > 0 
                          ? player.position.join(", ") 
                          : "Unknown"}
                    </div>
                  </div>

                  <div className="relative w-full aspect-[5/4] min-w-[140px]">
                    <SoccerPitch 
                      positions={player.position || []}
                      specificPositions={player.specific_positions || []}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- BOTTOM SECTION ---------------- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 w-full min-w-full">
            {/* column 1 */}
            <div className="lg:col-span-3">
              <Button
                onClick={() => {
                  if (!player.email) {
                    toast({
                      title: "No email address",
                      description: "This player doesn't have an email address on file.",
                      variant: "destructive",
                    });
                    return;
                  }
                  setShowEmailModal(true);
                }}
                className="w-full py-2 mb-3 bg-blue-500 hover:bg-blue-600 text-white text-sm"
              >
                <i className="fas fa-envelope mr-2" /> Email Player
              </Button>

              {/* resources */}
              <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm mb-3">
                <h4 className="text-sm font-semibold text-neutral-800 mb-1">
                  Player Resources
                </h4>

                {/* videos */}
                <div className="mb-2">
                  <h5 className="text-sm font-medium text-neutral-700 mb-0.5 flex items-center">
                    <i className="fas fa-video text-red-500 mr-1" />
                    Highlight Videos
                  </h5>
                  <div className="max-h-[95px] overflow-y-auto bg-neutral-50 rounded-md p-1.5 border border-neutral-100">
                    {player.highlight_videos?.length ? (
                      <div className="space-y-0.5">
                        {player.highlight_videos.map((v, i) => (
                          <a
                            key={v}
                            href={v}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-0.5 px-1.5 hover:bg-neutral-100 rounded text-sm"
                          >
                            <i className="fas fa-play-circle text-red-500 mr-1.5" />
                            Video {i + 1}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-neutral-500 py-0.5">
                        No videos available
                      </p>
                    )}
                  </div>
                </div>

                {/* attachments */}
                <div>
                  <h5 className="text-sm font-medium text-neutral-700 mb-0.5 flex items-center">
                    <i className="fas fa-paperclip text-blue-500 mr-1" />
                    Attachments
                  </h5>
                  <div className="max-h-[95px] overflow-y-auto bg-neutral-50 rounded-md p-1.5 border border-neutral-100">
                    {player.attachments?.length ? (
                      player.attachments.map((att) => {
                        const icon = (u: string) =>
                          u.endsWith(".pdf")
                            ? "fas fa-file-pdf text-red-500"
                            : /\.(docx?|rtf)$/.test(u)
                            ? "fas fa-file-word text-blue-500"
                            : /\.(xlsx?|csv)$/.test(u)
                            ? "fas fa-file-excel text-green-500"
                            : /\.(jpe?g|png|gif)$/.test(u)
                            ? "fas fa-file-image text-purple-500"
                            : "fas fa-file text-neutral-500";
                        return (
                          <a
                            key={att}
                            href={att}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-0.5 px-1.5 hover:bg-neutral-100 rounded text-sm"
                          >
                            <i className={`${icon(att)} mr-1.5`} />
                            <span className="truncate">
                              {att.split("/").pop()}
                            </span>
                          </a>
                        );
                      })
                    ) : (
                      <p className="text-xs text-neutral-500 py-0.5">
                        No attachments available
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* date added */}
              <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm">
                <h4 className="text-sm font-semibold text-neutral-800 mb-1 flex items-center">
                  <i className="fas fa-calendar-alt text-indigo-500 mr-1.5" />
                  Date Added
                </h4>
                <p className="text-sm font-medium text-neutral-800">
                  {player.date_added
                    ? new Date(player.date_added).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "Unknown"}
                </p>
              </div>
            </div>

            {/* column 2 */}
            <div className="lg:col-span-9">
              <div className="bg-white p-2 rounded-lg border border-neutral-200 shadow-sm h-full">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-neutral-800">
                    {showPreviousEmail ? "Previous Email Content" : "Original Email Content"}
                  </h4>
                  {currentPlayer.has_unread_update && currentPlayer.previous_email_content && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPreviousEmail(!showPreviousEmail)}
                      className="flex items-center gap-2 text-sm"
                    >
                      {showPreviousEmail ? (
                        <>
                          <ToggleRight className="h-4 w-4" />
                          Show Current
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-4 w-4" />
                          Show Previous
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {/* Email date info */}
                {currentPlayer.has_unread_update && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <i className="fas fa-clock" />
                    {showPreviousEmail ? (
                      <span>Previous message sent before {currentPlayer.last_email_date ? new Date(currentPlayer.last_email_date).toLocaleDateString() : 'recent update'}</span>
                    ) : (
                      <span>Latest message sent on {currentPlayer.last_email_date ? new Date(currentPlayer.last_email_date).toLocaleDateString() : 'recently'}</span>
                    )}
                  </div>
                )}
                
                <div className={`border rounded-md p-2 text-sm text-neutral-700 h-[250px] overflow-y-auto whitespace-pre-wrap ${
                  showPreviousEmail ? 'border-amber-200 bg-amber-50' : 'border-neutral-200 bg-neutral-50'
                }`}>
                  {showPreviousEmail ? (
                    currentPlayer.previous_email_content || (
                      <div className="text-neutral-500 italic">
                        <p>No previous email content available.</p>
                      </div>
                    )
                  ) : (
                    player.email_content ? (
                      <>
                        {player.email_content}
                        {/* Check if this might be a follow-up email that overwrote the original */}
                        {(player.email_update_count ?? 0) > 1 && !player.previous_email_content && (
                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                            <i className="fas fa-info-circle mr-2"></i>
                            <strong>Note:</strong> This may be a follow-up email. The original recruitment email might have been replaced before our recent system update. 
                            Check the "Recent Messages" section below for the complete conversation history.
                          </div>
                        )}
                      </>
                    ) : player.email ? (
                      player.email
                    ) : (
                      <div className="text-neutral-500 italic">
                        <p>No email content available for this player.</p>
                        <div className="mt-2 text-xs">
                          <p><strong>Debug info:</strong></p>
                          <p>• Player ID: {player.id}</p>
                          <p>• Email field: {player.email ? 'Present' : 'Missing'}</p>
                          <p>• Email content field: {player.email_content ? 'Present' : 'Missing'}</p>
                          <p>• Date added: {player.date_added}</p>
                          <details className="mt-2">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800">Show all player fields</summary>
                            <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                              {JSON.stringify(player, null, 2)}
                            </pre>
                          </details>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Message History Section */}
            <div className="lg:col-span-12 mt-4">
              <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Recent Messages
                    {messagesQuery.data && messagesQuery.data.length > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {messagesQuery.data.length}
                      </Badge>
                    )}
                  </h4>
                  {messagesQuery.data && messagesQuery.data.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onClose();
                        window.location.href = `/players?tab=messages&playerId=${player.id}`;
                      }}
                      className="text-xs"
                    >
                      View All Messages
                    </Button>
                  )}
                </div>
                
                {messagesQuery.isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <i className="fas fa-spinner fa-spin text-neutral-400"></i>
                  </div>
                ) : messagesQuery.data && messagesQuery.data.length > 0 ? (
                  <div className="space-y-2">
                    {messagesQuery.data.map((message: PlayerMessage) => (
                      <div
                        key={message.id}
                        className={`p-3 rounded-lg border transition-colors cursor-pointer hover:bg-neutral-50 ${
                          !message.is_read ? 'border-cyan-400 bg-cyan-50/30' : 'border-neutral-200'
                        }`}
                        onClick={() => {
                          onClose();
                          window.location.href = `/players?tab=messages&messageId=${message.id}`;
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{message.subject}</span>
                              {!message.is_read && (
                                <Badge variant="default" className="text-xs">New</Badge>
                              )}
                              {message.has_new_info && (
                                <Badge variant="secondary" className="text-xs">Has updates</Badge>
                              )}
                            </div>
                            <p className="text-xs text-neutral-600 line-clamp-2 mb-1">
                              {message.body_preview}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-neutral-500">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {format(new Date(message.email_date), "MMM d, yyyy")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-neutral-500">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No recent messages from this player</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <AlertDialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save these changes to {player.name}'s data? 
              This will update the player information and mark it as manually corrected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdits}>
              Yes, Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <PlayerEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        player={currentPlayer}
      />
    </>
  );
}
